import type {
  ConsentManagerConfig,
  ConsentManagerAPI,
  ConsentData,
} from '@/core/types';
import { ConsentManager } from '@/core/consent-manager';
import { ConsentBanner } from '@/ui/banner/consent-banner';
import { ConsentModal } from '@/ui/modal/consent-modal';
import { FloatingButton } from '@/ui/floating-button/floating-button';
import { ScriptBlocker } from '@/scripts/script-blocker';
import { ScriptInterceptor } from '@/scripts/script-interceptor';
import { GoogleConsentIntegration } from '@/integrations/google-consent';
import { GTMIntegration } from '@/integrations/gtm-integration';
import { TranslationManager } from '@/i18n/translation-manager';
import { LanguageDetector } from '@/i18n/language-detector';

declare global {
  interface Window {
    ConsentManager?: ConsentManagerAPI;
    __cmp?: ConsentManagerAPI;
  }
}

class CustomConsentManager implements ConsentManagerAPI {
  private core: ConsentManager;
  private banner: ConsentBanner | null = null;
  private modal: ConsentModal | null = null;
  private floatingButton: FloatingButton | null = null;
  private scriptBlocker: ScriptBlocker;
  private scriptInterceptor: ScriptInterceptor | null = null;
  private googleConsent: GoogleConsentIntegration;
  private gtmIntegration: GTMIntegration;
  private translationManager: TranslationManager;
  private config: ConsentManagerConfig;

  constructor(config: ConsentManagerConfig) {
    this.config = config;

    this.translationManager = new TranslationManager(config.customTranslations);

    if (config.language) {
      this.translationManager.setLanguage(config.language);
    } else if (config.autoDetectLanguage !== false) {
      const detectedLang = LanguageDetector.detectLanguage();
      this.translationManager.setLanguage(detectedLang);
    }

    this.core = new ConsentManager(config);
    this.scriptBlocker = new ScriptBlocker(config);

    // Initialize script interceptor if enabled (disabled by default for compatibility)
    if (config.enableScriptInterceptor === true) {
      this.scriptInterceptor = ScriptInterceptor.getInstance();
      // Start interceptor immediately to catch early scripts
      this.scriptInterceptor.startIntercepting();
      if (config.debugging) {
        console.log(
          '🔧 ConsentManager v1.0.2: Script Interceptor initialized and started'
        );
      }
    }
    this.googleConsent = GoogleConsentIntegration.getInstance(
      config.projectId,
      config.debugging
    );
    this.gtmIntegration = GTMIntegration.getInstance(
      config.projectId,
      config.debugging
    );

    this.initialize();
  }

  private async initialize(): Promise<void> {
    // Initialize Google Consent Mode FIRST, before everything else
    // This ensures consent defaults are set before any GTM scripts load
    this.googleConsent.initialize();
    this.gtmIntegration.initialize();

    this.core.addEventListener('ready', () => {
      // Check if user already has consent and show floating button
      const existingConsent = this.core.getConsent();
      if (existingConsent) {
        this.showFloatingButton();
      }

      // Update script interceptor with consent data if enabled
      if (this.scriptInterceptor) {
        const consent = this.core.getConsent();
        this.scriptInterceptor.updateConsent(
          consent || {
            essential: true,
            analytics: false,
            advertising: false,
            functional: false,
            personalization: false,
            timestamp: Date.now(),
            version: '1.0',
          }
        );
        if (this.config.debugging) {
          console.log(
            'ConsentManager: Script Interceptor updated with consent'
          );
        }
      }
    });

    this.core.addEventListener('banner_shown', () => {
      this.showBannerInternal();
      this.googleConsent.trackConsentInteraction('banner_shown');
      this.gtmIntegration.pushConsentEvent('banner_shown');
    });

    this.core.addEventListener('banner_hidden', () => {
      this.hideBannerInternal();
      this.gtmIntegration.pushConsentEvent('banner_hidden');
    });

    this.core.addEventListener('modal_shown', () => {
      this.gtmIntegration.pushConsentEvent('modal_shown');
    });

    this.core.addEventListener('modal_hidden', () => {
      this.gtmIntegration.pushConsentEvent('modal_hidden');
    });

    this.core.addEventListener('consent_given', (consent: ConsentData) => {
      this.updateIntegrations(consent);
      this.gtmIntegration.pushConsentEvent('consent_given', { consent });
      this.showFloatingButton();
    });

    this.core.addEventListener('consent_updated', (consent: ConsentData) => {
      this.updateIntegrations(consent);
      this.gtmIntegration.pushConsentUpdate(consent);
    });

    this.core.addEventListener('consent_withdrawn', () => {
      this.scriptBlocker.startBlocking();
      this.hideFloatingButton();

      if (this.scriptInterceptor) {
        this.scriptInterceptor.updateConsent({
          essential: true,
          analytics: false,
          advertising: false,
          functional: false,
          personalization: false,
          timestamp: Date.now(),
          version: '1.0',
        });
      }

      this.gtmIntegration.pushConsentEvent('consent_withdrawn');
    });
  }

  private updateIntegrations(consent: ConsentData): void {
    this.googleConsent.updateConsent(consent);
    this.gtmIntegration.setupConsentModeIntegration(consent);
    this.scriptBlocker.updateConsent(consent);

    if (this.scriptInterceptor) {
      this.scriptInterceptor.updateConsent(consent);
    }
  }

  private showBannerInternal(): void {
    if (this.banner) return;

    const translation = this.translationManager.getTranslation();

    this.banner = new ConsentBanner(this.config, translation, {
      onAcceptAll: () => {
        this.acceptAll();
        this.googleConsent.trackConsentInteraction('banner_accept');
      },
      onRejectAll: () => {
        this.rejectAll();
        this.googleConsent.trackConsentInteraction('banner_reject');
      },
      onCustomize: () => {
        this.showModal();
        this.googleConsent.trackConsentInteraction('banner_customize');
      },
    });

    this.banner.show();
  }

  private hideBannerInternal(): void {
    if (this.banner) {
      this.banner.hide();
      setTimeout(() => {
        this.banner = null;
      }, 350);
    }
  }

  public showModal(): void {
    const currentConsent = this.core.getConsent() || {
      essential: true,
      analytics: false,
      advertising: false,
      functional: false,
      personalization: false,
      timestamp: Date.now(),
      version: '1.0',
    };

    const translation = this.translationManager.getTranslation();

    this.modal = new ConsentModal(this.config, translation, currentConsent, {
      onSave: (consent: ConsentData) => {
        this.updateConsent(consent);
        this.hideModalInternal();
        this.hideBannerInternal();
      },
      onClose: () => {
        this.hideModalInternal();
      },
    });

    this.modal.show();
    this.core.emit('modal_shown');
  }

  private hideModalInternal(): void {
    if (this.modal) {
      this.modal.hide();
      setTimeout(() => {
        this.modal = null;
      }, 350);
      this.core.emit('modal_hidden');
    }
  }

  private showFloatingButton(): void {
    if (this.config.showFloatingButton === false) return;
    if (this.floatingButton) return;

    const translation = this.translationManager.getTranslation();

    this.floatingButton = new FloatingButton(this.config, translation, () => {
      this.showModal();
    });

    // Show floating button after banner is dismissed
    setTimeout(() => {
      this.floatingButton?.show();
    }, 1000);
  }

  // Method available for future use (e.g., when user revokes consent)
  private hideFloatingButton(): void {
    if (this.floatingButton) {
      this.floatingButton.hide();
      setTimeout(() => {
        this.floatingButton = null;
      }, 350);
    }
  }

  // Public API Implementation
  public getConsent(): ConsentData | null {
    return this.core.getConsent();
  }

  public async updateConsent(consent: Partial<ConsentData>): Promise<void> {
    await this.core.updateConsent(consent);
  }

  public showBanner(): void {
    this.core.showBanner();
  }

  public hideBanner(): void {
    this.core.hideBanner();
  }

  public hideModal(): void {
    this.hideModalInternal();
  }

  public async revokeConsent(): Promise<void> {
    await this.core.revokeConsent();
  }

  public async acceptAll(): Promise<void> {
    await this.core.acceptAll();
  }

  public async rejectAll(): Promise<void> {
    await this.core.rejectAll();
  }

  public onConsentChange(callback: (consent: ConsentData) => void): () => void {
    return this.core.onConsentChange(callback);
  }

  public addEventListener(event: any, callback: Function): () => void {
    return this.core.addEventListener(event, callback);
  }

  public getVersion(): string {
    return this.core.getVersion();
  }

  public getProjectId(): string {
    return this.core.getProjectId();
  }

  public isReady(): boolean {
    return this.core.isReady();
  }

  // Additional utility methods
  public changeLanguage(language: string): void {
    const translation = this.translationManager.setLanguage(language);

    if (this.banner) {
      this.banner.updateTranslation(translation);
    }

    if (this.floatingButton) {
      this.floatingButton.updateTranslation(translation);
    }
  }

  public getSupportedLanguages(): Array<{ code: string; name: string }> {
    return this.translationManager.getSupportedLanguages();
  }

  public getCurrentLanguage(): string {
    return this.translationManager.getCurrentLanguage();
  }
}

// Auto-initialization from script tag attributes
function autoInitialize(): CustomConsentManager | null {
  console.log('🍪 ConsentManager v1.0.2 - GDPR Compliant | autoInitialize() called');

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    console.log('ConsentManager: Window or document not available');
    return null;
  }

  const scriptTag = document.querySelector(
    'script[src*="consent"]'
  ) as HTMLScriptElement;
  if (!scriptTag) {
    console.warn(
      'ConsentManager: Script tag not found for auto-initialization'
    );
    return null;
  }

  console.log('ConsentManager: Found script tag:', scriptTag.src);

  const projectId =
    scriptTag.getAttribute('data-project') ||
    scriptTag.getAttribute('data-project-id');

  if (!projectId) {
    console.error('ConsentManager: data-project attribute is required');
    return null;
  }

  const apiEndpoint = scriptTag.getAttribute('data-api-endpoint');
  const position = scriptTag.getAttribute('data-position') as
    | 'top'
    | 'bottom'
    | null;
  const theme = scriptTag.getAttribute('data-theme') as
    | 'light'
    | 'dark'
    | 'auto'
    | null;
  const language = scriptTag.getAttribute('data-language');

  const enableScriptInterceptorAttr = scriptTag.getAttribute(
    'data-enable-script-interceptor'
  );
  console.log(
    'ConsentManager: data-enable-script-interceptor attribute:',
    enableScriptInterceptorAttr
  );
  console.log(
    'ConsentManager: enableScriptInterceptor will be:',
    enableScriptInterceptorAttr === 'true'
  );

  const config: ConsentManagerConfig = {
    projectId,
    ...(apiEndpoint && { apiEndpoint }),
    position: position || 'bottom',
    theme: theme || 'light',
    ...(language && { language }),
    autoDetectLanguage:
      scriptTag.getAttribute('data-auto-detect-language') !== 'false',
    showFloatingButton:
      scriptTag.getAttribute('data-show-floating-button') !== 'false',
    enableScriptInterceptor: enableScriptInterceptorAttr === 'true',
    debugging: scriptTag.getAttribute('data-debug') === 'true',
  };

  console.log('🍪 ConsentManager v1.0.2 - Final config:', config);

  try {
    const manager = new CustomConsentManager(config);
    console.log('✅ ConsentManager v1.0.2 initialized successfully! (GDPR Compliant)');

    // Expose to global scope
    window.ConsentManager = manager;
    window.__cmp = manager;

    return manager;
  } catch (error) {
    console.error('ConsentManager: Initialization failed', error);
    return null;
  }
}

// Initialize immediately if DOM is ready, otherwise wait
console.log('ConsentManager: DOM readyState:', document.readyState);
if (document.readyState === 'loading') {
  console.log('ConsentManager: Waiting for DOMContentLoaded');
  document.addEventListener('DOMContentLoaded', autoInitialize);
} else {
  console.log('ConsentManager: DOM ready, calling autoInitialize immediately');
  autoInitialize();
}

// Export for manual initialization
export {
  CustomConsentManager as ConsentManager,
  CustomConsentManager as default,
};
export * from '@/core/types';
